import React, { useState } from "react";
import { Clock } from "lucide-react";
import NeumorphicAnalogPreviewSvg from "./NeumorphicAnalogPreviewSvg";
import LongformAnalogPreviewSvg from "./LongformAnalogPreviewSvg";
import LiquidCrystalClock from "./LiquidCrystalClock";
import "./clockwidget.css";

const MOON_SVG = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="1em"
    height="1em"
    viewBox="0 0 16 16"
    fill="currentColor"
    className="de-clock-moon"
    aria-hidden
  >
    <path d="M6 .278a.768.768 0 0 1 .08.858 7.208 7.208 0 0 0-.878 3.46c0 4.021 3.278 7.277 7.318 7.277.527 0 1.04-.055 1.533-.16a.787.787 0 0 1 .81.316.733.733 0 0 1-.031.893A8.349 8.349 0 0 1 8.344 16C3.734 16 0 12.286 0 7.71 0 4.266 2.114 1.312 5.124.06A.752.752 0 0 1 6 .278z" />
    <path d="M10.794 3.148a.217.217 0 0 1 .412 0l.387 1.162c.173.518.579.924 1.097 1.097l1.162.387a.217.217 0 0 1 0 .412l-1.162.387a1.734 1.734 0 0 0-1.097 1.097l-.387 1.162a.217.217 0 0 1-.412 0l-.387-1.162A1.734 1.734 0 0 0 9.31 6.593l-1.162-.387a.217.217 0 0 1 0-.412l1.162-.387a1.734 1.734 0 0 0 1.097-1.097l.387-1.162zM13.863.099a.145.145 0 0 1 .274 0l.258.774c.115.346.386.617.732.732l.774.258a.145.145 0 0 1 0 .274l-.774.258a1.156 1.156 0 0 0-.732.732l-.258.774a.145.145 0 0 1-.274 0l-.258-.774a1.156 1.156 0 0 0-.732-.732l-.774-.258a.145.145 0 0 1 0-.274l.774-.258c.346-.115.617-.386.732-.732L13.863.1z" />
  </svg>
);

function formatPreviewNow() {
  const now = new Date();
  const t = now.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
  const m = t.match(/^(\d{1,2}:\d{2})\s*(AM|PM)/i);
  const timeMain = m ? m[1] : t;
  const timeSub = m ? m[2].toUpperCase() : "";
  const wd = now.toLocaleDateString("en-US", { weekday: "long" });
  const month = now.toLocaleDateString("en-US", { month: "long" });
  const d = now.getDate();
  const ord = (n) => {
    const s = ["th", "st", "nd", "rd"];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  };
  const dayLine = `${wd}, ${month} ${ord(d)}`;
  return { timeMain, timeSub, dayLine };
}

/**
 * @param {{ onAddClockWidget: (variant: string) => void }} props
 */
function ClockWidgetPicker({ onAddClockWidget }) {
  const [open, setOpen] = useState(false);
  const { timeMain, timeSub, dayLine } = formatPreviewNow();

  return (
    <div className="de-clock-picker">
      <button
        type="button"
        className="de-clock-picker-btn"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <Clock size={18} aria-hidden />
        Add clock widget
      </button>

      {open && (
        <div className="de-clock-options">
          <p className="de-clock-options-title">Choose style</p>
          <div className="de-clock-option-grid">
            <div>
              <button
                type="button"
                className="de-clock-card-preview"
                onClick={() => {
                  onAddClockWidget?.("gradient-night");
                  setOpen(false);
                }}
              >
                {MOON_SVG}
                <p className="de-clock-time-row">
                  <span>{timeMain}</span>
                  <span className="de-clock-time-sub">{timeSub}</span>
                </p>
                <p className="de-clock-day">{dayLine}</p>
              </button>
              <p className="de-clock-option-label">Gradient night</p>
            </div>
            <div>
              <button
                type="button"
                className="de-clock-card-preview de-clock-card-preview--analog"
                onClick={() => {
                  onAddClockWidget?.("neumorphic-analog");
                  setOpen(false);
                }}
              >
                <NeumorphicAnalogPreviewSvg />
              </button>
              <p className="de-clock-option-label">Soft neumorphic analog</p>
            </div>
            <div>
              <button
                type="button"
                className="de-clock-card-preview de-clock-card-preview--lcd"
                onClick={() => {
                  onAddClockWidget?.("liquid-crystal");
                  setOpen(false);
                }}
              >
                <div className="de-clock-lcd-preview-inner">
                  <LiquidCrystalClock className="de-clock-lcd-preview-clock" />
                </div>
              </button>
              <p className="de-clock-option-label">Liquid crystal digital</p>
            </div>
            <div>
              <button
                type="button"
                className="de-clock-card-preview de-clock-card-preview--longform"
                onClick={() => {
                  onAddClockWidget?.("longform-analog");
                  setOpen(false);
                }}
              >
                <LongformAnalogPreviewSvg />
              </button>
              <p className="de-clock-option-label">Longform text analog</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ClockWidgetPicker;
